import { createTheme } from '@mui/material/styles';

import * as tokens from './videa-style-tokens';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    buttonMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    headingMedium: React.CSSProperties;
    headingSmall: React.CSSProperties;
    headingExtrasmall: React.CSSProperties;
    bodySmall: React.CSSProperties;
    bodyExtrasmall: React.CSSProperties;
    labelSmallStrong: React.CSSProperties;
    labelExtrasmall: React.CSSProperties;
    headingLarge: React.CSSProperties;
    headingLabel: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    buttonMedium?: React.CSSProperties;
    labelSmall?: React.CSSProperties;
    headingMedium?: React.CSSProperties;
    headingSmall?: React.CSSProperties;
    headingExtrasmall?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
    bodyExtrasmall?: React.CSSProperties;
    labelSmallStrong?: React.CSSProperties;
    labelExtrasmall?: React.CSSProperties;
    headingLarge?: React.CSSProperties;
    headingLabel?: React.CSSProperties;
  }

  interface Palette {
    annotation: Palette['primary'];
  }

  interface PaletteOptions {
    annotation: PaletteOptions['primary'];
  }

  interface SimplePaletteColorOptions {
    red?: string;
    orange?: string;
    yellow?: string;
    green?: string;
    white?: string;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    buttonMedium: true;
    labelSmall: true;
    headingMedium: true;
    headingSmall: true;
    headingExtrasmall: true;
    bodySmall: true;
    bodyExtrasmall: true;
    labelSmallStrong: true;
    labelExtrasmall: true;
    headingLarge: true;
    headingLabel: true;
  }
}

export const theme = createTheme({
  spacing: (spacing: number) => `${spacing * 0.5}em`,
  palette: {
    mode: 'light',
    primary: {
      main: tokens.blueUltramarine500,
      dark: tokens.blueUltramarine800,
      light: tokens.blueUltramarine100,
      contrastText: tokens.white,
    },
    background: {
      default: tokens.backgroundDefault,
      paper: tokens.backgroundSubtle,
    },
    common: {
      black: tokens.black,
      white: tokens.white,
    },
    divider: tokens.foregroundSubtle,
    error: {
      dark: tokens.red600,
      light: tokens.red100,
      main: tokens.red400,
      contrastText: tokens.white,
    },
    info: {
      dark: tokens.blue700,
      light: tokens.blue200,
      main: tokens.blue500,
      contrastText: tokens.white,
    },
    secondary: {
      main: tokens.blueBlack640,
      dark: tokens.blueBlack650,
      light: tokens.blueBlack400,
      contrastText: tokens.white,
    },
    success: {
      dark: tokens.green400,
      light: tokens.green200,
      main: tokens.green300,
      contrastText: tokens.white,
    },
    text: {
      disabled: tokens.greyLight700,
      primary: tokens.blueBlack950,
      secondary: tokens.blueBlack650,
    },
    warning: {
      dark: tokens.orange700,
      light: tokens.yellowOrange100,
      main: tokens.yellowOrange300,
      contrastText: tokens.black,
    },
    annotation: {
      main: tokens.blue100,
      red: tokens.red400,
      orange: tokens.orange700,
      yellow: tokens.yellow500,
      green: tokens.green300,
      white: tokens.white,
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: tokens.fontFamiliesProximaNova,
    overline: {
      fontFamily: tokens.overlineFontFamily,
      fontWeight: tokens.overlineFontWeight,
      fontSize: tokens.overlineFontSize,
      lineHeight: tokens.overlineLineHeight,
      letterSpacing: tokens.overlineLetterSpacing,
    },
    labelSmall: {
      fontFamily: tokens.labelSmallFontFamily,
      fontWeight: tokens.labelSmallFontWeight,
      fontSize: tokens.labelSmallFontSize,
      lineHeight: tokens.labelSmallLineHeight,
      letterSpacing: tokens.labelSmallLetterSpacing,
    },
    buttonMedium: {
      fontFamily: tokens.buttonMediumFontFamily,
      fontWeight: tokens.buttonMediumFontWeight,
      fontSize: tokens.buttonMediumFontSize,
      lineHeight: tokens.buttonMediumLineHeight,
      letterSpacing: tokens.buttonMediumLetterSpacing,
    },
    headingMedium: {
      fontFamily: tokens.headingMediumFontFamily,
      fontWeight: tokens.headingMediumFontWeight,
      fontSize: tokens.headingMediumFontSize,
      lineHeight: tokens.headingMediumLineHeight,
      letterSpacing: tokens.headingMediumLetterSpacing,
    },
    headingSmall: {
      fontFamily: tokens.headingSmallFontFamily,
      fontWeight: tokens.headingSmallFontWeight,
      fontSize: tokens.headingSmallFontSize,
      lineHeight: tokens.headingSmallLineHeight,
      letterSpacing: tokens.headingSmallLetterSpacing,
    },
    headingExtrasmall: {
      fontFamily: tokens.headingExtrasmallFontFamily,
      fontWeight: tokens.headingExtrasmallFontWeight,
      fontSize: tokens.headingExtrasmallFontSize,
      lineHeight: tokens.headingExtrasmallLineHeight,
      letterSpacing: tokens.headingExtrasmallLetterSpacing,
    },
    bodySmall: {
      fontFamily: tokens.bodySmallFontFamily,
      fontWeight: tokens.bodySmallFontWeight,
      fontSize: tokens.bodySmallFontSize,
      lineHeight: tokens.bodySmallLineHeight,
      letterSpacing: tokens.bodySmallLetterSpacing,
    },
    bodyExtrasmall: {
      fontFamily: tokens.bodyExtrasmallFontFamily,
      fontWeight: tokens.bodyExtrasmallFontWeight,
      fontSize: tokens.bodyExtrasmallFontSize,
      lineHeight: tokens.bodyExtrasmallLineHeight,
      letterSpacing: tokens.bodyExtrasmallLetterSpacing,
    },
    labelSmallStrong: {
      fontFamily: tokens.labelSmallStrongFontFamily,
      fontWeight: tokens.labelSmallStrongFontWeight,
      fontSize: tokens.labelSmallStrongFontSize,
      lineHeight: tokens.labelSmallStrongLineHeight,
      letterSpacing: tokens.labelSmallStrongLetterSpacing,
    },
    labelExtrasmall: {
      fontFamily: tokens.labelExtrasmallFontFamily,
      fontWeight: tokens.labelExtrasmallFontWeight,
      fontSize: tokens.labelExtrasmallFontSize,
      lineHeight: tokens.labelExtrasmallLineHeight,
      letterSpacing: tokens.labelExtrasmallLetterSpacing,
    },
    headingLarge: {
      fontFamily: tokens.headingLargeFontFamily,
      fontWeight: tokens.headingLargeFontWeight,
      fontSize: tokens.headingLargeFontSize,
      lineHeight: tokens.headingLargeLineHeight,
      letterSpacing: tokens.headingLargeLetterSpacing,
    },
    headingLabel: {
      fontFamily: tokens.headingLargeFontFamily,
      fontWeight: tokens.fontWeightsProximaNovaLight,
      fontSize: tokens.headingMediumFontSize,
      lineHeight: tokens.headingMediumLineHeight,
      letterSpacing: tokens.bodySmallLetterSpacing,
    },
  },
});
